/* eslint-disable no-prototype-builtins */
import { DocumentNode, gql, useQuery } from '@fluentsms/graphql';
import { taxIdRegexValidation } from '@fluentsms/utils';
import { CircularProgress, IconButton, InputAdornment, InputBaseComponentProps, TextField } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { FunctionComponent } from 'react';
import ReactInputMask from 'react-input-mask';
import { TaxIdErrorState } from './TaxIdFieldGroupV2';

interface ExposeFieldProps {
  value?: string;
  variables?: any;
  query?: DocumentNode;
  hasTaxIdPermission?: boolean;
  error?: TaxIdErrorState;
  onBlur?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  label?: string;
  mask: string;
  isEditing?: boolean;
  isVisible?: boolean;
  taxIdKey?: string;
  fallbackValue?: string;
  regex?: RegExp;
  isExplicitSave?: boolean;
  explicitSaveEditedValue?: string;
  dataTestId?: string;
}

const traverseObject = (obj: Record<string, unknown>, key?: string, regex?: RegExp): string => {
  if (key !== undefined && obj?.hasOwnProperty(key)) {
    return obj[key] as string;
  }
  for (const i in obj) {
    if (obj[i] !== null && typeof obj[i] === 'object') {
      const result = traverseObject(obj[i] as Record<string, unknown>, key);
      if (result?.match(regex ?? taxIdRegexValidation)) {
        return result;
      }
    }
  }
  return '';
};

export const ProtectedField = (props: ExposeFieldProps) => {
  const {
    isVisible,
    variables,
    query,
    onChange,
    error,
    value = '',
    onBlur,
    label,
    mask,
    isEditing,
    taxIdKey = 'taxIdProtected',
    hasTaxIdPermission,
    fallbackValue,
    isExplicitSave = false,
    explicitSaveEditedValue,
    regex,
    dataTestId,
  } = props;
  const IdFieldProps = { 'data-testid': 'protectedTaxId-number' };
  const EyeBallProps = { 'data-testid': 'protectedTaxId-eyeball' };
  const { data, loading } = useQuery(
    gql`
      ${query}
    `,
    // if user creating new company and party is not created yet we skipp
    {
      ...variables,
      skip: (isExplicitSave && explicitSaveEditedValue) || variables.partyId ? !variables.partyId : null,
      fetchPolicy: 'no-cache',
    },
  );

  const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
    if (!loading) {
      onChange?.(event);
      onBlur?.();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // needed to keep tax id in case user revealed it and change tab immediately
    if (!loading) onChange?.(event);
  };

  const revealedValue = traverseObject(data, taxIdKey, regex);
  const taxIdValue = isEditing ? value : revealedValue.length ? revealedValue : fallbackValue;

  return (
    <>
      <TextField
        fullWidth
        autoFocus
        label={label}
        {...IdFieldProps}
        value={isExplicitSave && explicitSaveEditedValue ? explicitSaveEditedValue : taxIdValue}
        InputLabelProps={{ shrink: isVisible }}
        onBlur={handleBlur}
        error={error?.hasError}
        helperText={error?.message}
        onChange={handleChange}
        InputProps={{
          inputComponent: (ReactInputMask as unknown) as FunctionComponent<InputBaseComponentProps>,
          inputProps: {
            inputcomponent: ReactInputMask,
            mask: mask,
            'data-testid': dataTestId,
          },
          endAdornment: loading ? (
            <IconButton size="small" disabled>
              <CircularProgress style={{ width: 20, height: 20 }} />
            </IconButton>
          ) : hasTaxIdPermission ? (
            <InputAdornment position="end" tabIndex={-1}>
              <IconButton
                size="small"
                tabIndex={-1}
                {...EyeBallProps}
                disabled={taxIdValue === '' || error?.hasError}
                onMouseDown={(event) => {
                  event.preventDefault();
                  onBlur?.();
                }}
              >
                {!isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
    </>
  );
};
